/**
 * Centralized Visibility Manager
 * Single source of truth for visibility changes with debouncing and Safari detection
 * Prevents multiple independent visibility handlers from causing cascading unmounts
 * 
 * DEBUG: Use window.__visibilityDebug() in browser console to see status
 */

type VisibilityCallback = (isVisible: boolean) => void;

interface SubscriberInfo {
  callback: VisibilityCallback;
  label: string;
  subscribedAt: number;
}

interface VisibilityEvent {
  timestamp: number;
  isVisible: boolean;
  subscriberCount: number;
}

class VisibilityManager {
  private subscribers = new Map<VisibilityCallback, SubscriberInfo>();
  private debounceTimeout: NodeJS.Timeout | null = null;
  private lastState: boolean | null = null;
  private isInitialized = false;
  private eventHistory: VisibilityEvent[] = [];
  private maxHistorySize = 50;
  
  // Safari detection
  private isSafari = typeof navigator !== 'undefined' 
    ? /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
    : false;
  
  // Safari needs longer debounce to avoid aggressive tab suspension issues
  private get debounceDelay(): number {
    return this.isSafari ? 500 : 250;
  }
  
  init() {
    if (this.isInitialized) return;
    
    const handleVisibilityChange = () => {
      if (this.debounceTimeout) clearTimeout(this.debounceTimeout);
      
      const rawState = !document.hidden;
      const eventTime = Date.now();
      
      console.log(`👁️ [VisibilityManager] Raw event: ${rawState ? 'VISIBLE' : 'HIDDEN'} (debouncing ${this.debounceDelay}ms, ${this.subscribers.size} subscribers)`);
      
      this.debounceTimeout = setTimeout(() => {
        const isVisible = !document.hidden;
        
        // Only notify if state actually changed
        if (isVisible !== this.lastState) {
          this.lastState = isVisible;
          const direction = isVisible ? 'VISIBLE' : 'HIDDEN';
          
          // Log event to history
          this.eventHistory.push({
            timestamp: eventTime,
            isVisible,
            subscriberCount: this.subscribers.size
          });
          
          // Trim history
          if (this.eventHistory.length > this.maxHistorySize) {
            this.eventHistory = this.eventHistory.slice(-this.maxHistorySize);
          }
          
          console.log(`👁️ [VisibilityManager] Tab ${direction} - notifying ${this.subscribers.size} subscribers`);
          
          // Notify all subscribers
          this.subscribers.forEach((info, callback) => {
            try {
              console.log(`  → Notifying: ${info.label}`);
              callback(isVisible);
            } catch (err) {
              console.error(`[VisibilityManager] Error in subscriber "${info.label}":`, err);
            }
          });
        } else {
          console.log(`👁️ [VisibilityManager] State unchanged (${isVisible ? 'visible' : 'hidden'}), skipping notification`);
        }
      }, this.debounceDelay);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    this.isInitialized = true;
    console.log(`🎬 [VisibilityManager] Initialized (Safari: ${this.isSafari}, debounce: ${this.debounceDelay}ms)`);
    
    // Expose debug function globally
    if (typeof window !== 'undefined') {
      (window as any).__visibilityDebug = () => this.debug();
    }
  }

  /**
   * Subscribe to visibility changes
   * @param callback Function to call when visibility changes
   * @param label Label for debugging (e.g., component name)
   * @returns Unsubscribe function
   */
  subscribe(callback: VisibilityCallback, label: string = 'Anonymous'): () => void {
    this.init();
    
    const info: SubscriberInfo = {
      callback,
      label,
      subscribedAt: Date.now()
    };
    
    this.subscribers.set(callback, info);
    console.log(`➕ [VisibilityManager] Subscriber added: "${label}" (total: ${this.subscribers.size})`);
    
    // Immediately call with current state if we know it
    if (this.lastState !== null) {
      console.log(`  → Initial state for "${label}": ${this.lastState ? 'visible' : 'hidden'}`);
      callback(this.lastState);
    }
    
    return () => {
      this.subscribers.delete(callback);
      console.log(`➖ [VisibilityManager] Subscriber removed: "${label}" (total: ${this.subscribers.size})`);
    };
  }

  /**
   * Get current visibility state
   */
  getIsVisible(): boolean {
    return !document.hidden;
  }
  
  /**
   * Check if running on Safari
   */
  isSafariBrowser(): boolean {
    return this.isSafari;
  }
  
  /**
   * Debug information - call via window.__visibilityDebug()
   */
  debug(): void {
    console.group('🔍 VisibilityManager Debug Info');
    console.log('Status:', {
      initialized: this.isInitialized,
      isSafari: this.isSafari,
      debounceDelay: this.debounceDelay,
      currentState: this.lastState !== null ? (this.lastState ? 'visible' : 'hidden') : 'unknown',
      subscriberCount: this.subscribers.size
    });
    
    console.log('\n📋 Subscribers:');
    this.subscribers.forEach((info) => {
      const age = Math.round((Date.now() - info.subscribedAt) / 1000);
      console.log(`  • ${info.label} (subscribed ${age}s ago)`);
    });
    
    console.log('\n📜 Recent Events (last 10):');
    this.eventHistory.slice(-10).forEach((event) => {
      const time = new Date(event.timestamp).toLocaleTimeString();
      console.log(`  • ${time}: ${event.isVisible ? 'VISIBLE' : 'HIDDEN'} (${event.subscriberCount} subscribers)`);
    });
    
    console.groupEnd();
  }
}

export const visibilityManager = new VisibilityManager();
